import { useMemo } from "react";
import { useAuth } from "@/contexts/AuthContext";
import { brl } from "@/lib/api";
import { useHideValues } from "@/components/HideValues";
import { useProductCosts, profitOf } from "@/components/BudgetProfit";
import { TrendingUp } from "lucide-react";

export function profitTone(pct, thresholds) {
  if (pct == null) return "text-zinc-400";
  if (pct < 0) return "text-red-300";
  if (pct >= (thresholds?.good ?? 30)) return "text-green-300";
  if (pct >= (thresholds?.warn ?? 15)) return "text-amber-300";
  return "text-red-300";
}

export function profitBorder(pct, thresholds) {
  if (pct == null) return "border-zinc-800 bg-zinc-950/40";
  if (pct < 0) return "border-red-800 bg-red-950/30";
  if (pct >= (thresholds?.good ?? 30)) return "border-green-brand/50 bg-green-brand/10";
  if (pct >= (thresholds?.warn ?? 15)) return "border-amber-700 bg-amber-950/20";
  return "border-red-800 bg-red-950/30";
}

export function computeProfit(items, costMap) {
  const rows = (items || []).map((it) => profitOf(it, costMap));
  const cost = rows.reduce((s, r) => s + (r ? r.cost : 0), 0);
  const revenueKnown = rows.reduce((s, r) => s + (r ? r.revenue : 0), 0);
  const revenueAll = (items || []).reduce((s, it) => s + (Number(it.quantity) || 0) * (Number(it.unit_price) || 0) - (Number(it.discount) || 0), 0);
  const profit = revenueKnown - cost;
  const pct = revenueKnown > 0 ? (profit / revenueKnown) * 100 : null;
  const hasService = rows.some((r) => !r || r.missing);
  const missing = rows.filter((r) => !r || r.missing).length;
  return { cost, revenueKnown, revenueAll, profit, pct, hasService, missing };
}

export function ProfitBoard({ order, items: itemsProp, title = "Rentabilidade da OS" }) {
  const { user } = useAuth();
  const { hide } = useHideValues();
  const costMap = useProductCosts();
  const thresholds = user?.shop?.profit_thresholds || { good: 30, warn: 15 };
  const items = itemsProp || order?.budget?.items || [];
  const d = useMemo(() => computeProfit(items, costMap), [items, costMap]);
  if (!items.length) return null;
  const fmt = (v) => (hide ? "•••••" : brl(v));
  const tone = d.missing ? "text-amber-300" : profitTone(d.pct, thresholds);
  const border = d.missing ? "border-amber-700 bg-amber-950/20" : profitBorder(d.pct, thresholds);
  return (
    <div data-testid="profit-board" className={`rounded-xl border ${border} px-3 py-2 sm:px-4 sm:py-3`}>
      <div className="flex items-center justify-between gap-2 flex-wrap">
        <div className="flex items-center gap-2 min-w-0">
          <TrendingUp className={`h-4 w-4 shrink-0 ${tone}`} />
          <span className="text-[11px] font-mono-caps text-zinc-400">{title}</span>
          <span className="text-[10px] text-zinc-500 hidden sm:inline">(só a oficina vê · bom ≥ {thresholds.good}% · apertado ≥ {thresholds.warn}%)</span>
        </div>
        <span data-testid="profit-board-margin" className={`font-display font-black text-xl sm:text-2xl tabular-nums ${tone}`}>
          {d.pct == null ? "—" : `${d.pct.toFixed(1)}%${d.missing ? "?" : ""}`}
        </span>
      </div>
      <div className="mt-1 grid grid-cols-3 gap-2 text-xs sm:text-sm">
        <div><div className="text-[10px] font-mono-caps text-zinc-500">Custo</div><div data-testid="profit-board-cost" className="font-bold text-zinc-200 tabular-nums">{fmt(d.cost)}</div></div>
        <div><div className="text-[10px] font-mono-caps text-zinc-500">Venda</div><div data-testid="profit-board-revenue" className="font-bold text-zinc-100 tabular-nums">{fmt(d.revenueAll)}</div></div>
        <div><div className="text-[10px] font-mono-caps text-zinc-500">Lucro</div><div data-testid="profit-board-profit" className={`font-bold tabular-nums ${tone}`}>{fmt(d.profit)}</div></div>
      </div>
      {d.missing > 0 && <p data-testid="profit-board-missing" className="mt-1 text-[11px] font-bold text-amber-300">{d.missing} {d.missing === 1 ? "item" : "itens"} sem custo — lucro pode estar errado. Preencha o custo de cada item.</p>}
    </div>
  );
}

export function ItemMarginChip({ item, costMap, testid, thresholds }) {
  const { hide } = useHideValues();
  const p = profitOf(item, costMap);
  if (!p || p.pct == null) {
    return <span data-testid={testid} className="text-[10px] text-zinc-500">—</span>;
  }
  const tone = profitTone(p.pct, thresholds);
  return <span data-testid={testid} className={`text-[11px] font-bold tabular-nums ${tone}`} title={`Custo ${hide ? "•••" : brl(p.cost)} · Lucro ${hide ? "•••" : brl(p.profit)}`}>{p.pct.toFixed(1)}%</span>;
}
